import { makeAutoObservable } from 'mobx';
import IDealPortfolio, { IDealPortfolioState } from '../interfaces/IDealPortfolio';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import DealPortfolioApiService from '../services/dealPortfolioApiService';

export default class DealPortfolioStore {
    portfolios: IDealPortfolio[] = [];
    isPortfoliosLoading: ILoadingProps = {};

    constructor(
        private dealPortfolioApiService: DealPortfolioApiService,
    ) {
        makeAutoObservable(this);
    }

    setPortfolios(value: IDealPortfolio[]) {
        this.portfolios = value;
    }

    setIsPortfoliosLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isPortfoliosLoading.fetching = value,
            creating: (value: boolean) => this.isPortfoliosLoading.creating = value,
            updating: (value: boolean) => this.isPortfoliosLoading.updating = value,
            deleting: (value: boolean) => this.isPortfoliosLoading.deleting = value,
        };
    }

    getPortfolioForDeal(dealId: number): IDealPortfolio | null {
        return this.portfolios.find(p => p.dealIds.includes(dealId)) ?? null;
    }

    initialize() {
        this.setIsPortfoliosLoading().fetching(true);
        this.dealPortfolioApiService.getPortfolios().then(portfolios => {
            this.setPortfolios(portfolios);
        }).finally(() => {
            this.setIsPortfoliosLoading().fetching(false);
        });
    }

    async createPortfolio(state: IDealPortfolioState): Promise<IDealPortfolio> {
        this.setIsPortfoliosLoading().creating(true);
        try {
            const portfolio = await this.dealPortfolioApiService.createPortfolio(state);
            this.setPortfolios([...this.portfolios, portfolio]);
            return portfolio;
        } finally {
            this.setIsPortfoliosLoading().creating(false);
        }
    }

    async updatePortfolio(portfolio: IDealPortfolio): Promise<IDealPortfolio> {
        this.setIsPortfoliosLoading().updating(true);
        try {
            const updated = await this.dealPortfolioApiService.updatePortfolio(portfolio);
            this.setPortfolios([
                ...this.portfolios.filter(p => p.id !== portfolio.id),
                updated,
            ]);
            return updated;
        } finally {
            this.setIsPortfoliosLoading().updating(false);
        }
    }

    async deletePortfolio(id: number): Promise<void> {
        this.setIsPortfoliosLoading().deleting(true);
        try {
            await this.dealPortfolioApiService.deletePortfolio(id);
            this.setPortfolios(this.portfolios.filter(p => p.id !== id));
        } finally {
            this.setIsPortfoliosLoading().deleting(false);
        }
    }

    clear() {
        this.portfolios = [];
        this.isPortfoliosLoading = {};
    }
}
